// Per-tenant broadcast-capability override controls (segmentation slice).
// Used by both TenantCreate and TenantEditDrawer.
//
// A tenant inherits its broadcast segments + filter-builder access from its
// plan tier. This component lets an admin OVERRIDE that for one tenant: when
// the override is off, both values are null (inherit the plan); when on, the
// admin sets an explicit segment list + filter-builder flag.

import {
  BROADCAST_PRESET_META,
  BROADCAST_SEGMENT_KEYS,
  type BroadcastSegmentKey,
} from '@naya/shared'

interface Props {
  /** null = inherit the plan tier; an array = explicit override. */
  presetsEnabled: BroadcastSegmentKey[] | null
  /** null = inherit the plan tier; a boolean = explicit override. */
  filterBuilderEnabled: boolean | null
  onChange: (
    presets: BroadcastSegmentKey[] | null,
    filterBuilder: boolean | null,
  ) => void
}

function segmentLabel(key: BroadcastSegmentKey): string {
  if (key === 'everyone') return 'Everyone'
  if (key === 'birthday_this_month') return 'Birthday this month'
  return BROADCAST_PRESET_META[key].label
}

export function BroadcastCapabilityFields({
  presetsEnabled,
  filterBuilderEnabled,
  onChange,
}: Props) {
  // The override is "active" when either column carries an explicit value.
  // Both move together: turning the override on/off sets/clears both.
  const overrideActive =
    presetsEnabled !== null || filterBuilderEnabled !== null

  const presets = presetsEnabled ?? []
  const builder = filterBuilderEnabled ?? false

  return (
    <div className="admin-field">
      <label className="admin-label">Broadcast capabilities</label>
      <label className="admin-check-row">
        <input
          type="checkbox"
          checked={overrideActive}
          onChange={(e) =>
            e.target.checked
              ? onChange([], false)
              : onChange(null, null)
          }
        />
        Override the plan tier for this tenant
      </label>

      {!overrideActive && (
        <p className="admin-hint" style={{ marginTop: 4 }}>
          This tenant inherits the broadcast segments and filter builder from
          its plan tier.
        </p>
      )}

      {overrideActive && (
        <>
          <p className="admin-hint" style={{ marginLeft: 22, marginTop: 6 }}>
            Curated segments this tenant can target:
          </p>
          {BROADCAST_SEGMENT_KEYS.map((seg) => (
            <label
              key={seg}
              className="admin-check-row"
              style={{ marginLeft: 22 }}
            >
              <input
                type="checkbox"
                checked={presets.includes(seg)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...presets, seg]
                      : presets.filter((s) => s !== seg),
                    builder,
                  )
                }
              />
              {segmentLabel(seg)}
            </label>
          ))}
          <label
            className="admin-check-row"
            style={{ marginLeft: 22, marginTop: 8 }}
          >
            <input
              type="checkbox"
              checked={builder}
              onChange={(e) => onChange(presets, e.target.checked)}
            />
            Custom filter builder (power-user audience builder)
          </label>
        </>
      )}
    </div>
  )
}
